import React, { useEffect, useState } from 'react';

import { Eye, EyeOff, X } from 'lucide-react';

import { MemberTypeConstants } from '../../constants/MemberTypeConstants';
import { useAuthApi } from '../../hooks/useAuthApi';
import { useMemberApi } from '../../hooks/useMemberApi';
import { useUserApi } from '../../hooks/useUserApi';
import { AuthDTO } from '../../services/apiClient';
import { setTokens } from '../../utils/tokenUtils';

// The login / become-a-member gate (D7 and D11 in docs/EXPLORE.md).
//
// D7: browsing Explore never asks for a login, but the first *write* does. The moment a signed-out
// visitor marks a course played or adds it to a bucket list — from the detail drawer, the list card
// or the nearby strip — this comes up instead of the tick.
//
// D11: it is a drawer rather than a modal or a route push, so one component serves desktop and
// phone, and so the map, the camera and the open course all stay alive behind it.
//
// It holds the two real paths: **sign in** and **sign up**. Sign-up is D13's Quick path, cut down to
// the shortest form that can exist: first name, last name, email, password. No phone number (it is
// optional, and an optional field on a sign-up form is a field asking to be deleted) and no price,
// because there is nothing to pay. Every new member lands on the free tier and only meets a price
// when they run out of free played courses, at which point UpgradeDrawer takes over.
//
// Deliberately not built on components/common/Drawer.tsx for the same reason CourseDetailDrawer is
// not: that one is modal, and its full-screen backdrop and page-scroll lock swallow map panning.
// The forms are hand-rolled rather than using components/common/FormField, which is styled for the
// light admin screens and reads as a foreign object on this charcoal panel.

// 'forgot' is the reset-password request, kept inside the drawer rather than sent off to the
// /forgot-password route: the same D11 reason the gate is a drawer at all, the map and the open
// course stay alive behind it. The emailed link still lands on the /reset-password page.
type Mode = 'signIn' | 'signUp' | 'forgot';

interface MemberSetupDrawerProps {
  isOpen: boolean;
  // Which tab the drawer opens on. Defaults to sign in, which is right for the D7 gate: the
  // visitor who ticked a course usually already has an account. The public site sends people
  // here from its "Become a member" button instead (?reg=1 on /explore), and they want the
  // sign-up form, so Explore passes signUp for that arrival.
  initialMode?: 'signIn' | 'signUp';
  // What the visitor was trying to do when the gate stopped them, so the copy can name it. There
  // is deliberately no pending action queued behind it: D10 says they tick again after logging in,
  // and nothing is replayed on their behalf.
  intent: string | null;
  onClose: () => void;
  // Fired once tokens are stored. Explore uses it to close the gate and let the visitor tick again.
  onAuthenticated?: () => void;
}

// A failed call arrives in one of two shapes. For a response the swagger types (register's 400),
// NSwag throws the ApiResponse body itself, a plain { success, message } object. For one it does
// not type, it throws an ApiException, which may carry that body as `result`. Either way the real
// reason (Identity's password rules, for one) is the backend's message, not the generic Error text.
const messageFrom = (err: unknown, fallback: string): string => {
  const failure = err as {
    message?: unknown;
    result?: { message?: unknown };
  } | null;
  const apiMessage = failure?.result?.message ?? failure?.message;
  if (typeof apiMessage === 'string' && apiMessage.trim().length > 0) {
    return apiMessage;
  }
  return fallback;
};

const fieldClasses =
  'h-11 w-full rounded-lg border border-primary/25 bg-black/30 px-3 text-sm text-white outline-none transition-colors placeholder:text-white/30 focus:border-primary focus:ring-1 focus:ring-primary disabled:opacity-60';

const labelClasses =
  'mb-1.5 block text-xs font-medium uppercase tracking-wider text-white/50';

interface PasswordInputProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete: 'current-password' | 'new-password';
  placeholder?: string;
  minLength?: number;
}

// The dark-panel twin of components/common/formElements/PasswordField: same eye toggle, but styled
// for this charcoal drawer instead of the light admin screens. Each instance holds its own
// visibility state, so revealing the sign-up password does not also reveal the confirmation.
const PasswordInput: React.FC<PasswordInputProps> = ({
  id,
  label,
  value,
  onChange,
  autoComplete,
  placeholder,
  minLength,
}) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div>
      <label htmlFor={id} className={labelClasses}>
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          type={isVisible ? 'text' : 'password'}
          autoComplete={autoComplete}
          required
          minLength={minLength}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={`${fieldClasses} pr-11`}
        />
        <button
          type="button"
          onClick={() => setIsVisible((prev) => !prev)}
          aria-label={isVisible ? 'Hide password' : 'Show password'}
          aria-pressed={isVisible}
          className="absolute inset-y-0 right-0 flex items-center px-3 text-white/40 transition-colors hover:text-white"
        >
          {isVisible ? (
            <EyeOff className="h-4 w-4" />
          ) : (
            <Eye className="h-4 w-4" />
          )}
        </button>
      </div>
    </div>
  );
};

const MemberSetupDrawer: React.FC<MemberSetupDrawerProps> = ({
  isOpen,
  initialMode = 'signIn',
  intent,
  onClose,
  onAuthenticated,
}) => {
  const { generateToken, generateTokenTwoFactor } = useAuthApi();
  const { register } = useMemberApi();
  const { forgotPassword } = useUserApi();

  const [mode, setMode] = useState<Mode>(initialMode);
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Set when sign-up is refused because the address already has a login (the backend's 409). It
  // gets its own message with a way into the sign-in tab, rather than a dead-end error line.
  const [emailTaken, setEmailTaken] = useState(false);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // 2FA is an existing account's setting, so the sign-in path has to handle it even though nothing
  // in sign-up can switch it on. Same two-step exchange the /login page does.
  const [tempToken, setTempToken] = useState<string | null>(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');

  // Closing throws the half-typed form away rather than leaving a stale password sitting in memory
  // for the next time the gate trips.
  useEffect(() => {
    if (isOpen) {
      // Opening picks the tab up again: the drawer is mounted for the whole life of the page, so
      // a caller that changes initialMode before opening it has to be obeyed here, not only at
      // first render.
      setMode(initialMode);
      return;
    }
    setMode(initialMode);
    setError(null);
    setNotice(null);
    setEmailTaken(false);
    setPassword('');
    setConfirmPassword('');
    setTempToken(null);
    setTwoFactorCode('');
  }, [isOpen, initialMode]);

  const switchMode = (next: Mode): void => {
    setMode(next);
    setError(null);
    setNotice(null);
    setEmailTaken(false);
    setTempToken(null);
    setTwoFactorCode('');
  };

  // The one place tokens are stored. No navigation: the visitor stays on the map, the gate closes,
  // and under D10 they tick again themselves.
  const finishSignIn = (auth: AuthDTO): void => {
    setTokens(auth.accessToken ?? null, auth.refreshToken ?? null, false);
    setPassword('');
    setTempToken(null);
    setTwoFactorCode('');
    onAuthenticated?.();
    onClose();
  };

  const handleSignIn = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setNotice(null);
    setIsBusy(true);

    try {
      const response = await generateToken({ email: email.trim(), password });

      if (!response?.success || !response.data) {
        setError('Invalid email or password.');
        return;
      }

      const auth = response.data as unknown as AuthDTO;
      if (auth.twoFactorEnabled) {
        setTempToken(auth.accessToken ?? null);
        return;
      }

      finishSignIn(auth);
    } catch (err: unknown) {
      setError(messageFrom(err, 'Could not sign you in. Please try again.'));
    } finally {
      setIsBusy(false);
    }
  };

  const handleTwoFactor = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setIsBusy(true);

    try {
      if (!tempToken) {
        setError(
          'That sign-in has timed out. Please enter your password again.',
        );
        return;
      }

      const response = await generateTokenTwoFactor({
        code: twoFactorCode,
        tempToken,
        email: email.trim(),
      });

      if (!response?.success || !response.data) {
        setError('That code was not accepted. Please try again.');
        return;
      }

      finishSignIn(response.data as unknown as AuthDTO);
    } catch (err: unknown) {
      setError(
        messageFrom(err, 'Could not check that code. Please try again.'),
      );
    } finally {
      setIsBusy(false);
    }
  };

  // The backend answers "User not found." for an unknown address, which would tell anyone with the
  // drawer open whether an email is registered. The same neutral line is shown either way, and the
  // visitor is put back on the sign-in form to wait for the mail.
  const handleForgotPassword = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setNotice(null);
    setIsBusy(true);

    const neutralNotice =
      'If that email address has an account, a reset link is on its way. Check your inbox.';

    try {
      await forgotPassword({ email: email.trim() });
      setMode('signIn');
      setNotice(neutralNotice);
    } catch {
      setMode('signIn');
      setNotice(neutralNotice);
    } finally {
      setIsBusy(false);
    }
  };

  // Sign-up logs the person in straight away: the account exists the moment it succeeds, and the
  // backend hands back the same token pair GenerateToken does, so there is no reason to make them
  // re-type the password they just chose. Nothing about money happens here: the new member is on
  // the free tier, and the drawer simply closes onto the map they were already using.
  const handleSignUp = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setNotice(null);
    setEmailTaken(false);

    if (password !== confirmPassword) {
      setError('The two passwords do not match.');
      return;
    }

    setIsBusy(true);

    try {
      const response = await register({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        password,
        // Sent rather than left out, so the payload says which membership this account is on
        // instead of that being invisible knowledge inside the backend. RegisterAsync accepts
        // only this one, so it is a statement of fact, not a choice the form is offering.
        memberTypeId: MemberTypeConstants.FREE_ID,
      });

      if (!response?.success) {
        setError(response?.message ?? 'Could not create that account.');
        return;
      }

      setPassword('');
      setConfirmPassword('');

      const auth = response.data?.auth;
      if (auth?.accessToken) {
        setTokens(auth.accessToken, auth.refreshToken ?? null, false);
        onAuthenticated?.();
      }

      onClose();
    } catch (err: unknown) {
      // NSwag's ApiException carries the HTTP status.
      if ((err as { status?: number })?.status === 409) {
        setEmailTaken(true);
        return;
      }
      setError(messageFrom(err, 'Could not create that account.'));
    } finally {
      setIsBusy(false);
    }
  };

  const isTwoFactorStep = tempToken !== null;

  return (
    // Rendered unconditionally and translated off-screen rather than unmounted, so it can animate
    // both ways — the same reason CourseDetailDrawer stays mounted. aria-hidden and inert-by-pointer
    // while closed so a keyboard or screen reader cannot reach a drawer nobody can see.
    <aside
      aria-hidden={!isOpen}
      aria-label="Become a member"
      className={`fixed inset-y-0 right-0 z-[60] flex w-full flex-col border-l border-primary/20 bg-charcoal shadow-2xl transition-transform duration-300 ease-in-out sm:w-96 ${
        isOpen ? 'translate-x-0' : 'pointer-events-none translate-x-full'
      }`}
    >
      <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
        <p className="text-sm font-bold uppercase tracking-wider text-white">
          {mode === 'forgot'
            ? 'Reset your password'
            : mode === 'signUp'
              ? 'Create your account'
              : 'Become a member'}
        </p>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="rounded-lg p-1 text-white/50 transition-colors hover:bg-white/10 hover:text-white"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-5 pb-8 pt-5">
        {mode !== 'forgot' && (
          <>
            {/* Two arrivals, two openings. The D7 gate names the thing the visitor just tried to
                do; the public site sends a stranger who pressed "Become a member" and has no
                intent to name, so that one is greeted rather than told to sign in. */}
            {intent ? (
              <>
                <p className="text-base font-semibold text-white">
                  Sign in to save {intent}
                </p>
                <p className="mt-1.5 text-sm leading-relaxed text-white/50">
                  Browsing the map is open to everyone. Marking a course played
                  or adding it to a bucket list belongs to your GolfPlayed
                  membership.
                </p>
              </>
            ) : (
              // Opened from the 0 flag in Explore's top bar (or the public site's "Become a
              // member"), so it asks the flag's own question.
              <>
                <p className="text-base font-semibold text-white">
                  How many courses have you played?
                </p>
                <p className="mt-1.5 text-sm leading-relaxed text-white/50">
                  Create an account and find out today.
                </p>
              </>
            )}
          </>
        )}

        {mode === 'forgot' && (
          <>
            <p className="text-base font-semibold text-white">
              Forgot your password?
            </p>
            <p className="mt-1.5 text-sm leading-relaxed text-white/50">
              Give us your email address. We send you a link to set a new
              password.
            </p>
          </>
        )}

        {/* Hidden during the 2FA step and the post-signup payment step: switching tabs there
            would drop work that is already half done. */}
        {!isTwoFactorStep && mode !== 'forgot' && (
          <div className="mt-5 flex rounded-lg border border-white/10 bg-black/30 p-1">
            {(
              [
                ['signIn', 'Sign in'],
                ['signUp', 'Sign up'],
              ] as [Mode, string][]
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => switchMode(value)}
                className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  mode === value
                    ? 'bg-primary text-charcoal'
                    : 'text-white/60 hover:text-white'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        {notice && (
          <p className="mt-4 rounded-lg border border-primary/30 bg-primary/10 px-3 py-2 text-sm text-primary">
            {notice}
          </p>
        )}

        {/* Switching to sign in keeps the email field, so the visitor only types the password. */}
        {emailTaken && (
          <p
            role="alert"
            className="mt-4 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300"
          >
            An account with that email address already exists.{' '}
            <button
              type="button"
              onClick={() => switchMode('signIn')}
              className="font-semibold text-primary hover:underline"
            >
              Sign in here
            </button>
          </p>
        )}

        {error && (
          <p
            role="alert"
            className="mt-4 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300"
          >
            {error}
          </p>
        )}

        {isTwoFactorStep ? (
          <form onSubmit={handleTwoFactor} className="mt-5 space-y-4">
            <div>
              <label htmlFor="member-setup-2fa" className={labelClasses}>
                Code from your authenticator app
              </label>
              <input
                id="member-setup-2fa"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                required
                value={twoFactorCode}
                onChange={(e) => setTwoFactorCode(e.target.value)}
                placeholder="6-digit code"
                className={fieldClasses}
              />
            </div>
            <button
              type="submit"
              disabled={isBusy}
              className="h-11 w-full rounded-lg bg-primary text-sm font-semibold text-charcoal transition-opacity hover:opacity-90 disabled:cursor-wait disabled:opacity-60"
            >
              {isBusy ? 'Checking...' : 'Verify'}
            </button>
          </form>
        ) : mode === 'forgot' ? (
          <form onSubmit={handleForgotPassword} className="mt-5 space-y-4">
            <div>
              <label
                htmlFor="member-setup-forgot-email"
                className={labelClasses}
              >
                Email address
              </label>
              <input
                id="member-setup-forgot-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className={fieldClasses}
              />
            </div>
            <button
              type="submit"
              disabled={isBusy}
              className="h-11 w-full rounded-lg bg-primary text-sm font-semibold text-charcoal transition-opacity hover:opacity-90 disabled:cursor-wait disabled:opacity-60"
            >
              {isBusy ? 'Sending...' : 'Send reset link'}
            </button>
            <p className="text-center text-xs text-white/40">
              <button
                type="button"
                onClick={() => switchMode('signIn')}
                className="text-primary hover:underline"
              >
                Back to sign in
              </button>
            </p>
          </form>
        ) : mode === 'signIn' ? (
          <form onSubmit={handleSignIn} className="mt-5 space-y-4">
            <div>
              <label htmlFor="member-setup-email" className={labelClasses}>
                Email address
              </label>
              <input
                id="member-setup-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className={fieldClasses}
              />
            </div>
            <PasswordInput
              id="member-setup-password"
              label="Password"
              value={password}
              onChange={setPassword}
              autoComplete="current-password"
              placeholder="Your password"
            />
            <button
              type="submit"
              disabled={isBusy}
              className="h-11 w-full rounded-lg bg-primary text-sm font-semibold text-charcoal transition-opacity hover:opacity-90 disabled:cursor-wait disabled:opacity-60"
            >
              {isBusy ? 'Signing in...' : 'Sign in'}
            </button>
            <p className="text-center text-xs text-white/40">
              <button
                type="button"
                onClick={() => switchMode('forgot')}
                className="text-primary hover:underline"
              >
                Forgot your password?
              </button>
            </p>
            <p className="text-center text-xs text-white/40">
              No account yet?{' '}
              <button
                type="button"
                onClick={() => switchMode('signUp')}
                className="text-primary hover:underline"
              >
                Create one
              </button>
            </p>
          </form>
        ) : (
          <form onSubmit={handleSignUp} className="mt-5 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="member-setup-first" className={labelClasses}>
                  First name
                </label>
                <input
                  id="member-setup-first"
                  type="text"
                  autoComplete="given-name"
                  required
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className={fieldClasses}
                />
              </div>
              <div>
                <label htmlFor="member-setup-last" className={labelClasses}>
                  Last name
                </label>
                <input
                  id="member-setup-last"
                  type="text"
                  autoComplete="family-name"
                  required
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className={fieldClasses}
                />
              </div>
            </div>
            <div>
              <label
                htmlFor="member-setup-signup-email"
                className={labelClasses}
              >
                Email address
              </label>
              <input
                id="member-setup-signup-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className={fieldClasses}
              />
            </div>
            <PasswordInput
              id="member-setup-new-password"
              label="Password"
              value={password}
              onChange={setPassword}
              autoComplete="new-password"
              placeholder="At least 6 characters, one number"
              minLength={6}
            />
            <PasswordInput
              id="member-setup-confirm-password"
              label="Confirm password"
              value={confirmPassword}
              onChange={setConfirmPassword}
              autoComplete="new-password"
            />
            <button
              type="submit"
              disabled={isBusy}
              className="h-11 w-full rounded-lg bg-primary text-sm font-semibold text-charcoal transition-opacity hover:opacity-90 disabled:cursor-wait disabled:opacity-60"
            >
              {isBusy ? 'Creating account...' : 'Create account'}
            </button>
            {/* The free tier, said plainly and only once. The number is the backend's
                MembershipConstants.FreeCoursesPlayedLimit; it is written out here rather than
                fetched, because a sign-up form must not wait on a request to render its own
                reassurance. UpgradeDrawer quotes the real price when the limit is actually hit. */}
            <p className="text-center text-xs text-white/40">
              Free to join. Mark up to 30 courses played, then upgrade whenever
              you are ready.
            </p>
          </form>
        )}
      </div>
    </aside>
  );
};

export default MemberSetupDrawer;
